/**
 * Hook: Add/Update facility actions with toast and cache refetch for Cleaning Standards page.
 * Single Responsibility: call create/update facility, refetch queries, show success/error toast.
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createFacility, updateFacility } from '../../../services/facilitiesService';
import { FACILITIES_QUERY_KEY } from '../../../hooks/useFacilitiesInfinite';
import { FACILITIES_PAGE_BASED_QUERY_KEY } from '../../../hooks/useFacilitiesPageBased';
import { ERROR_MESSAGES, TOAST_CONFIG } from '../constants';

function refetchFacilitiesQueries(queryClient) {
  return Promise.all([
    queryClient.refetchQueries({ queryKey: FACILITIES_QUERY_KEY }),
    queryClient.refetchQueries({ queryKey: FACILITIES_PAGE_BASED_QUERY_KEY }),
  ]);
}

export function useCleaningStandardsActions(showToast) {
  const queryClient = useQueryClient();

  const handleAddFacility = useCallback(
    async (data) => {
      try {
        const response = await createFacility(data);
        const newFacility = response.data;
        await refetchFacilitiesQueries(queryClient);
        showToast({
          variant: 'success',
          title: TOAST_CONFIG.TITLE_SUCCESS,
          copy: `${newFacility.name} has been added to your organization`,
          duration: TOAST_CONFIG.ERROR_DURATION_MS,
        });
      } catch (error) {
        showToast({
          variant: 'error',
          title: TOAST_CONFIG.TITLE_ERROR,
          copy: error?.message || ERROR_MESSAGES.ADD_FACILITY,
          duration: TOAST_CONFIG.ERROR_DURATION_MS,
        });
        throw error;
      }
    },
    [showToast, queryClient],
  );

  const handleUpdateFacility = useCallback(
    async (facilityId, data) => {
      try {
        await updateFacility(facilityId, data);
        await refetchFacilitiesQueries(queryClient);
        showToast({
          variant: 'success',
          title: TOAST_CONFIG.TITLE_SUCCESS,
          copy: 'Facility updated successfully',
          duration: TOAST_CONFIG.SUCCESS_DURATION_MS,
        });
      } catch (error) {
        showToast({
          variant: 'error',
          title: TOAST_CONFIG.TITLE_ERROR,
          copy: error?.message || ERROR_MESSAGES.UPDATE_FACILITY,
          duration: TOAST_CONFIG.ERROR_DURATION_MS,
        });
        throw error;
      }
    },
    [showToast, queryClient],
  );

  return {
    handleAddFacility,
    handleUpdateFacility,
  };
}
